import { InlineMessage } from '@strata/react-inline-message';
import { useFormikContext } from 'formik';

import {
    type AutomationRule,
    AutomationRuleActions,
    AutomationRuleCriteria,
    type AutomationRuleFormContext,
} from 'services/AutomationRuleHelpers';

const CriteriaKeys = {
    [AutomationRuleCriteria.PAYER]: {
        ruleKey: 'segmentId',
        criterionKey: 'id',
    },
    [AutomationRuleCriteria.PRODUCT]: {
        ruleKey: 'productServiceOid',
        criterionKey: 'oid',
    },
    [AutomationRuleCriteria.PATIENT]: {
        ruleKey: 'memberId',
        criterionKey: 'id',
    },
    [AutomationRuleCriteria.PERFORMANCE]: {
        ruleKey: 'performance',
        criterionKey: 'id',
    },
    [AutomationRuleCriteria.PLACE_OF_SERVICE]: {
        ruleKey: 'placeOfService',
        criterionKey: 'id',
    },
    [AutomationRuleCriteria.PRESCRIBER_LIST]: {
        ruleKey: 'prescriberListId',
        criterionKey: 'prescriberListId',
    },
    [AutomationRuleCriteria.STATE_LIST]: {
        ruleKey: 'stateListId',
        criterionKey: 'stateListId',
    },
} as const;

/** Count the number of criteria a rule has defined */
const getNumberOfRuleCriteria = (rule: AutomationRule) =>
    Object.values(CriteriaKeys).reduce(
        (prev, curr) => prev + (rule[curr.ruleKey] != null ? 1 : 0),
        0,
    );

const findDuplicateRule = (
    values: AutomationRuleFormContext,
    ruleId?: string,
    rules?: AutomationRule[],
) =>
    rules?.find(
        (rule) =>
            rule.id !== ruleId &&
            // Check to make sure the # of criteria is equal
            values.criteria.length === getNumberOfRuleCriteria(rule) &&
            values.criteria.every(({ criterion, value }) => {
                // Check for both oid and isBrand if product
                if (criterion === AutomationRuleCriteria.PRODUCT) {
                    return (
                        rule.productServiceOid === value?.oid &&
                        (rule.isBrand === value?.isBrand ||
                            values.action.type === AutomationRuleActions.SEND_REFERRAL)
                    );
                }
                // if we don't have a mapping for the criterion, don't worry about it
                const keys = CriteriaKeys[criterion];
                if (!keys) {
                    return true;
                }
                // check if the expected values between rules match!
                return rule?.[keys?.ruleKey] === value?.[keys?.criterionKey as keyof typeof value];
            }),
    );

interface DuplicateRuleMessageProps {
    rule?: Partial<AutomationRule>;
    rules?: AutomationRule[];
    onChangeRule: (newRule: AutomationRule) => void;
}

export const DuplicateRuleMessage = ({
    rule = undefined,
    rules = undefined,
    onChangeRule,
}: DuplicateRuleMessageProps) => {
    const formik = useFormikContext<AutomationRuleFormContext>();
    const duplicate = findDuplicateRule(formik.values, rule?.id, rules);
    return duplicate ? (
        <InlineMessage
            intent={InlineMessage.Intent.ERROR}
            label={
                <>
                    <span>{`A rule with these conditions already exists. View `}</span>
                    <u
                        className="sds_automation-template__duplicate-link"
                        role="button"
                        tabIndex={0}
                        onClick={() => onChangeRule(duplicate)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                onChangeRule(duplicate);
                            }
                        }}
                    >
                        existing rule
                    </u>
                    <span>{` or update the conditions to create a new rule.`}</span>
                </>
            }
        />
    ) : null;
};
